import { useCallback, useRef, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL || '';
const RATE_LIMIT_MESSAGE = 'The minds are resting — too many questions at once. Try again in a minute.';

const createRateLimitError = () => {
  const error = new Error(RATE_LIMIT_MESSAGE);
  error.status = 429;
  return error;
};

const postJsonChat = async (payload) => {
  const response = await fetch(`${API}/api/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (response.status === 429) {
    throw createRateLimitError();
  }
  if (!response.ok) {
    throw new Error(`Chat request failed with status ${response.status}`);
  }

  const data = await response.json();
  if (typeof data?.content !== 'string') {
    throw new Error('Chat response did not include content');
  }

  return data;
};

const readStreamingChat = async (payload, onDelta) => {
  const response = await fetch(`${API}/api/chat/stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (response.status === 429) {
    throw createRateLimitError();
  }
  if (!response.ok) {
    throw new Error(`Streaming request failed with status ${response.status}`);
  }
  if (!response.body) {
    throw new Error('Streaming response body is unavailable');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let content = '';
  let doneEvent = null;

  const processFrame = (frame) => {
    for (const line of frame.split('\n')) {
      if (!line.startsWith('data:')) continue;

      const payloadText = line.slice(5).trimStart();
      if (!payloadText) continue;

      const event = JSON.parse(payloadText);
      if (event.type === 'delta') {
        if (typeof event.content !== 'string') {
          throw new Error('Streaming delta did not include content');
        }
        content += event.content;
        onDelta(event.content);
      } else if (event.type === 'done') {
        doneEvent = event;
      } else if (event.type === 'error') {
        throw new Error(event.message || 'Streaming response failed');
      }
    }
  };

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) {
        buffer += decoder.decode();
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      buffer = buffer.replace(/\r\n/g, '\n');

      let boundary = buffer.indexOf('\n\n');
      while (boundary !== -1) {
        processFrame(buffer.slice(0, boundary));
        buffer = buffer.slice(boundary + 2);
        boundary = buffer.indexOf('\n\n');
      }
    }

    buffer = buffer.replace(/\r\n/g, '\n');
    if (buffer.trim()) {
      processFrame(buffer);
    }

    if (!doneEvent) {
      throw new Error('Streaming response ended before completion');
    }

    return {
      id: doneEvent.id,
      mind_id: doneEvent.mind_id,
      metadata: doneEvent.metadata,
      content,
    };
  } catch (streamError) {
    await reader.cancel().catch(() => {});
    throw streamError;
  } finally {
    reader.releaseLock();
  }
};

const useStreamingChat = () => {
  const [streamingText, setStreamingText] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState(null);
  const inFlightRef = useRef(false);

  const sendMessage = useCallback(async ({ mindId, message, history = [] }) => {
    if (inFlightRef.current) {
      throw new Error('A chat response is already in progress');
    }

    const payload = {
      mind_id: mindId,
      message,
      conversation_history: history,
    };

    inFlightRef.current = true;
    setStreamingText('');
    setIsStreaming(true);
    setError(null);

    try {
      try {
        return await readStreamingChat(payload, (delta) => {
          setStreamingText((current) => current + delta);
        });
      } catch (streamError) {
        if (streamError?.status === 429) throw streamError;
        const fallbackResponse = await postJsonChat(payload);
        setStreamingText(fallbackResponse.content);
        return fallbackResponse;
      }
    } catch (requestError) {
      setStreamingText('');
      setError(requestError);
      throw requestError;
    } finally {
      inFlightRef.current = false;
      setIsStreaming(false);
    }
  }, []);

  return { streamingText, isStreaming, error, sendMessage };
};

export { useStreamingChat };
export default useStreamingChat;
